import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Place, PlaceInput, placesApi } from "../api/places";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";
import "./PlacesPage.css";

type FormState = { name: string; address: string; coords: string; notes: string };

const EMPTY_FORM: FormState = { name: "", address: "", coords: "", notes: "" };

const COORDS_HINT = "Широта и долгота через запятую, как их копируют из Яндекс Карт: 55.75580, 37.61730";

const hasCoords = (p: Place) => p.latitude != null && p.longitude != null;

const formOf = (p: Place): FormState => ({
  name: p.name,
  address: p.address ?? "",
  coords: hasCoords(p) ? `${p.latitude}, ${p.longitude}` : "",
  notes: p.notes ?? "",
});

type ParsedCoords =
  | { ok: true; latitude: number | null; longitude: number | null }
  | { ok: false; error: string };

// Decimal commas are not supported: the comma is what separates the two numbers.
function parseCoords(text: string): ParsedCoords {
  const trimmed = text.trim();
  if (trimmed === "") return { ok: true, latitude: null, longitude: null };
  const parts = trimmed.split(/[\s,;]+/).filter(Boolean);
  const [latitude, longitude] = parts.map(Number);
  if (parts.length !== 2 || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return { ok: false, error: "Нужны два числа через запятую: широта и долгота" };
  }
  if (Math.abs(latitude) > 90) return { ok: false, error: "Широта от -90 до 90" };
  if (Math.abs(longitude) > 180) return { ok: false, error: "Долгота от -180 до 180" };
  return { ok: true, latitude, longitude };
}

const byName = (a: Place, b: Place) => a.name.localeCompare(b.name, "ru");

export default function PlacesPage() {
  const [places, setPlaces] = useState<Place[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingID, setEditingID] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const formRef = useRef<HTMLFormElement>(null);
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const list = await placesApi.list();
      setPlaces((list ?? []).slice().sort(byName));
    } catch (err: any) {
      console.error("Failed to load places", err);
      setError(err?.message || "Не удалось загрузить площадки");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const editing = editingID === null ? null : places.find((p) => p.id === editingID) ?? null;
  const name = form.name.trim();
  const coords = parseCoords(form.coords);
  const canSubmit = !busy && name !== "" && coords.ok;

  const set = (changes: Partial<FormState>) => setForm((f) => ({ ...f, ...changes }));

  const startEditing = (p: Place) => {
    setError(null);
    setEditingID(p.id);
    setForm(formOf(p));
    formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const stopEditing = () => {
    setEditingID(null);
    setForm(EMPTY_FORM);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !coords.ok) return;
    const input: PlaceInput = {
      name,
      address: form.address.trim() || null,
      latitude: coords.latitude,
      longitude: coords.longitude,
      notes: form.notes.trim() || null,
    };
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        await placesApi.update(editing.id, input);
      } else {
        await placesApi.create(input);
      }
      stopEditing();
      await load();
    } catch (err: any) {
      console.error("Place save failed", err);
      setError(err?.message || (editing ? "Не удалось сохранить площадку" : "Не удалось добавить площадку"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p: Place) => {
    const confirmed = await confirm({
      title: `Удалить площадку «${p.name}»?`,
      body: (
        <>
          <p>Площадка пропадёт из списка, и её больше нельзя будет выбрать для тренировки.</p>
          <p>
            Тренировки, назначенные на неё, в том числе прошедшие, останутся, но без площадки: в
            приложении у них пропадут адрес и точка на карте.
          </p>
          <p>Отменить удаление нельзя.</p>
        </>
      ),
      confirmLabel: "Удалить площадку",
      danger: true,
    });
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await placesApi.remove(p.id);
      if (editingID === p.id) stopEditing();
    } catch (err: any) {
      console.error("Place delete failed", err);
      setError(err?.message || "Не удалось удалить площадку");
    } finally {
      setBusy(false);
      await load();
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="page page-wide">
      <h2>Площадки</h2>
      <p className="page-description">
        Залы и площадки, где проходят тренировки. Площадку выбирают для тренировки, и в приложении у
        тренировки видны её адрес и точка на карте.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      <form
        ref={formRef}
        className="mav-card cp-form place-form"
        aria-label={editing ? "Изменение площадки" : "Новая площадка"}
        onSubmit={submit}
      >
        <h3 className="place-form-title">{editing ? `Изменение: ${editing.name}` : "Новая площадка"}</h3>
        <label className="cp-field">
          <span>Название</span>
          <input className="mav-input" value={form.name} maxLength={100} disabled={busy} onChange={(e) => set({ name: e.target.value })} />
        </label>
        <label className="cp-field">
          <span>Адрес</span>
          <input className="mav-input" value={form.address} maxLength={200} disabled={busy} onChange={(e) => set({ address: e.target.value })} />
        </label>
        <div className="cp-field">
          <label className="cp-field">
            <span>Координаты</span>
            <input
              className={coords.ok ? "mav-input" : "mav-input has-error"}
              value={form.coords}
              inputMode="decimal"
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              onChange={(e) => set({ coords: e.target.value })}
            />
          </label>
          <span className={coords.ok ? "mav-hint" : "mav-hint is-error"}>{coords.ok ? COORDS_HINT : coords.error}</span>
        </div>
        <label className="cp-field">
          <span>Заметки</span>
          <input className="mav-input" value={form.notes} maxLength={300} disabled={busy} onChange={(e) => set({ notes: e.target.value })} />
        </label>
        <div className="cp-actions mav-actions">
          <button type="submit" className="mav-btn-primary" disabled={!canSubmit}>
            {editing ? "Сохранить" : "Добавить площадку"}
          </button>
          {editing && (
            <button type="button" className="mav-btn-secondary" disabled={busy} onClick={stopEditing}>
              Отмена
            </button>
          )}
        </div>
      </form>

      <div className="table-wrap">
        <table className="settings-table stack-table place-table">
          <thead>
            <tr>
              <th>Площадка</th>
              <th>Точка на карте</th>
              <th>Заметки</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {places.length === 0 ? (
              <tr>
                <td colSpan={4} className="empty-cell">
                  {error ? "—" : "Площадок пока нет"}
                </td>
              </tr>
            ) : (
              places.map((p) => (
                <tr key={p.id}>
                  <td className="cell-place-name">
                    <span className="place-name">{p.name}</span>
                    {p.address ? (
                      <span className="place-address">{p.address}</span>
                    ) : (
                      <span className="place-address mav-status-muted">Адрес не указан</span>
                    )}
                  </td>
                  <td className="cell-place-coords">
                    {hasCoords(p) ? (
                      <span>{`${p.latitude}, ${p.longitude}`}</span>
                    ) : (
                      <span className="mav-status-muted">Нет точки</span>
                    )}
                  </td>
                  <td className="cell-place-notes">{p.notes || <span className="mav-status-muted">—</span>}</td>
                  <td className="cell-action">
                    <div className="row-actions">
                      <button className="mav-btn-secondary" disabled={busy} onClick={() => startEditing(p)}>
                        Изменить
                      </button>
                      <button className="btn-quiet-danger" disabled={busy} onClick={() => remove(p)}>
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {dialog}
    </div>
  );
}
